import type { HealthMetric, HealthTable } from "@sauron/contracts"
import { useState } from "react"
import { Badge } from "@/components/ui/badge.js"
import { Button } from "@/components/ui/button.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { cn } from "@/lib/utils.js"
import { Stat, StatRow } from "./Page.js"

/**
 * Renders what a provider declared, without knowing what is in it.
 *
 * Both the health screen and the entity screen use these: a source reports
 * facts and tables as data, and the panel decides only how they look. That is
 * what lets an integration appear on both screens without a line changing
 * here.
 *
 * These are the contract's reporting vocabulary, not a third copy of it — a
 * source declares a metric or a table once, in `@sauron/contracts`, and it is
 * rendered here and published to `/metrics` from the same shape.
 */
export type DeclaredMetric = HealthMetric
export type DeclaredTableSpec = HealthTable

export function DeclaredMetrics({ metrics }: { metrics: readonly DeclaredMetric[] }) {
  if (metrics.length === 0) return null

  return (
    <StatRow>
      {metrics.map((metric) => (
        <Stat
          key={metric.key}
          size="md"
          label={metric.label}
          value={formatValue(metric)}
          tone={toneOf(metric.tone)}
          {...(metric.hint === undefined ? {} : { hint: metric.hint })}
        />
      ))}
    </StatRow>
  )
}

/**
 * `initialRows` caps what is shown until somebody asks for the rest. MF's scan
 * history is fourteen rows of six columns and the answer to "did last night's
 * scan run" is in the first one; the other thirteen are there for the morning
 * somebody is reconstructing a bad week, and they can wait for a click.
 */
export function DeclaredTable({
  table,
  initialRows,
}: {
  table: DeclaredTableSpec
  initialRows?: number
}) {
  const [expanded, setExpanded] = useState(false)

  if (table.rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{table.title}</span> — brak danych.
      </p>
    )
  }

  const capped = initialRows !== undefined && !expanded && table.rows.length > initialRows
  const rows = capped ? table.rows.slice(0, initialRows) : table.rows

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h4 className="text-[15px] font-semibold text-foreground">{table.title}</h4>
        {table.hint !== undefined && (
          <p className="text-[13px] text-muted-foreground">{table.hint}</p>
        )}
      </div>
      <Table>
        <thead>
          <tr>
            {table.columns.map((column) => (
              <Th key={column.key} className={cn(column.numeric === true && "text-right")}>
                {column.label}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            // The provider chose the columns, so the row's own values are the
            // only identity available here — there is no id to reach for.
            <Tr key={table.columns.map((column) => String(row[column.key])).join("|")}>
              {table.columns.map((column) => (
                <Td
                  key={column.key}
                  className={cn(column.numeric === true && "text-right font-mono text-[13px]")}
                >
                  {render(row[column.key])}
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
      {capped && (
        <Button variant="ghost" size="sm" onClick={() => setExpanded(true)}>
          Pokaż wszystkie ({table.rows.length})
        </Button>
      )}
    </div>
  )
}

/** The one thing a source puts next to the entity's name, if it has one. */
export function Headline({
  headline,
}: {
  headline: { label: string; tone: "neutral" | "good" | "warn" | "bad"; value?: string }
}) {
  const tone =
    headline.tone === "good"
      ? "allow"
      : headline.tone === "bad"
        ? "critical"
        : headline.tone === "warn"
          ? "prepay"
          : "neutral"
  const glyph = tone === "allow" ? "✓" : tone === "critical" ? "×" : tone === "prepay" ? "!" : "·"

  return (
    <Badge
      tone={tone}
      glyph={glyph}
      {...(headline.value === undefined ? {} : { title: headline.value })}
    >
      {headline.label}
    </Badge>
  )
}

function render(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean") return value ? "tak" : "nie"
  return String(value)
}

export function formatValue(metric: DeclaredMetric): string {
  if (metric.value === null) return "—"
  if (metric.format === "ratio" && typeof metric.value === "number") {
    return `${Math.round(metric.value * 100)}%`
  }
  /*
   * A gauge and a sentence have different readers. "Klucz skonfigurowany 0" is
   * the number an alert needs and a fact nobody can read; the provider says
   * which of the two this is, and the number itself is untouched for the
   * scrape.
   */
  if (metric.format === "flag") return metric.value === 0 ? "nie" : "tak"
  return String(metric.value)
}

/** The provider judges its own tone; the panel only translates it to a colour. */
export function toneOf(tone: DeclaredMetric["tone"]): "neutral" | "ember" | "allow" | "critical" {
  if (tone === "bad") return "critical"
  if (tone === "warn") return "ember"
  if (tone === "good") return "allow"
  return "neutral"
}
